/* global window*/
import { Div, reactive, Variable } from 'alkali'
/**
 * Create a legend on a chart
 * @module Legend
 * @type {string}
 * @exports default LabeledLegend
 */

//Defaults passed to legend
const DEFAULTS = {
	TITLE: 'Legend',
	COLORS: ['#000', '#fff'],
}

/**
 * Creates a legend on a chart
 * Note: either colorFunc must be defined for discrete labels, or colors mut be defined for continous labels
 * @name LabeledLegend
 * @param {DOMElement} {target} -- element on which to add legend
 * @param {String} {title} -- legend to be labeled
 * @param {Array<string>} {items} -- labels applied to colors of legends
 * @param {boolean} {[showAtStart = true]} -- display legend upon creation
 * @param {string} {[displayType = 'discrete']} -- discrete vs continuous display type
 * @param {function} {[colorFunc]} -- translates label to color (hex or rbg)
 * @param {Array<string>} {[colors]} -- colors used in gradient
 * @param {string} {[gradientOrientation = 'horizontal']} -- horizontal vs vertical gradient type when display type is continuous
 * @return {Alkali} -- Legend
 * Note:	colors and strings need not be the same length
 */

class Legend extends Div() {
	show: Variable<boolean>

	created(props) {
		const target = props.target
		const title = props.title || DEFAULTS.TITLE
		const items = props.items || []
		const displayType = props.displayType || 'discrete'
		const location = props.location
		const gradientOrientation = props.gradientOrientation || 'horizontal'
		const colorFunc = props.colorFunc || (c => c)
		const colors = props.colors || DEFAULTS.COLORS
		const showAtStart = props.showAtStart === undefined ? true : props.showAtStart
		const labels = []
		if (displayType === 'discrete') {
			for (const item of items) {
				labels.push(
					Div('.text-label', [
						Div('.marker', {
							attributes: {
								style: `background-color:${colorFunc(item)}`,
							},
						}),
						Div('.text', item),
					])
				)
			}
		} else if (displayType === 'continuous') {
			const gradient = Div('.gradient', {
				attributes: {
					style: `background: linear-gradient(to ${
						gradientOrientation === 'vertical' ? 'bottom' : 'right'
					}, ${colors.join(',')});`,
				},
			})
			labels.push(
				gradient,
				Div('.gradient-label', [
					Div('.text-label.min', items[0] || ''), // (left / bottom)
					Div('.text-label.max', items[items.length - 1] || 0), // (right / top)
				])
			)
		}
		this.append(title ? Div('.title', title) : undefined, Div('.labels', labels))
		this.show = reactive(showAtStart)
		props.display = this.show
		props.classes = {
			'legend-box': true,
			discrete: displayType === 'discrete',
			continuous: displayType === 'continuous',
			'location-top-left': location === 'top-left',
			'location-bottom-left': location === 'bottom-left',
			'location-top-right': location === 'top-right',
			'location-bottom-right': location === 'bottom-right',
			vertical: gradientOrientation === 'vertical',
			horizontal: gradientOrientation === 'horizontal',
		}
		if (target) {
			target.node().parentNode.append(this)
		}
	}
}
export default Legend
